import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Grid,
  TextField,
  Typography,
  Box,
} from '@mui/material'
import { LocalAtm } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import {
  getRefundedReceivedCheckDetails,
  refundReceivedCheck,
} from 'src/services/CheckService'
import { formatCurrency, formatDate } from 'src/utils/Formatter'
import { ReceivedCheckSummaryResult } from 'src/viewModels/ReceivedCheckSummaryResult'
import { RefundReceivedChecksRequest } from 'src/viewModels/RefundReceivedChecksRequest'

interface IRefundedCheckDetailsModal extends DialogProps {
  close: () => void
  check: ReceivedCheckSummaryResult
  hasRefundedCheckInformation?: boolean
  onRefundComplete?: () => void
}

const RefundedCheckDetailsModal: React.FC<IRefundedCheckDetailsModal> = ({
  close,
  check,
  hasRefundedCheckInformation,
  onRefundComplete,
  ...modalProps
}) => {
  const [loading, setLoading] = useState(true)
  const [refundDetails, setRefundDetails] = useState<RefundReceivedChecksRequest | null>(
    null
  )
  const [payer, setPayer] = useState('')
  const [refundCheckDate, setRefundCheckDate] = useState('')
  const [refundCheckNumber, setRefundCheckNumber] = useState('')
  const [refundNote, setRefundNote] = useState('')

  useEffect(() => {
    loadRefundDetails()
  }, [])

  const loadRefundDetails = () => {
    getRefundedReceivedCheckDetails(check.checkId)
      .then((response) => {
        const refundDate = response.refundCheckDate
          ? new Date(response.refundCheckDate).toISOString().split('T')[0]
          : ''

        setRefundDetails(response)
        setPayer(response.payer || '')
        setRefundCheckDate(refundDate)
        setRefundCheckNumber(response.refundCheckNumber || '')
        setRefundNote(response.refundNote || '')
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const executeRefund = () => {
    if (refundDetails) {
      const refundRequest = new RefundReceivedChecksRequest()
      refundRequest.checkNumber = refundDetails.checkNumber
      refundRequest.checkDate = refundDetails.checkDate
      refundRequest.refundAmount = refundDetails.refundAmount
      refundRequest.payer = payer
      refundRequest.refundCheckDate = refundCheckDate
      refundRequest.refundCheckNumber = refundCheckNumber
      refundRequest.refundNote = refundNote

      refundReceivedCheck(check.checkId, refundRequest).then(() => {
        if (onRefundComplete) {
          onRefundComplete()
        }
        close()
      })
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          close()
        }
      }}
      {...modalProps}
    >
      <ResponsiveAppBar
        title={`Refund Applied Funds to Client`}
        pageIcon={<LocalAtm />}
        isModal
      />
      <DialogContent style={{ overflowY: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : refundDetails ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" color="textSecondary" style={{ marginBottom: 8 }}>
                Refunding Client Funds for:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography variant="body1" style={{ minWidth: 60 }}>
                  <strong>Payer:</strong>
                </Typography>
                <TextField
                  value={payer}
                  onChange={(e) => setPayer(e.target.value)}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled={hasRefundedCheckInformation}
                />
              </div>
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="date"
                label="Refund Check Date"
                value={refundCheckDate}
                onChange={(e) => setRefundCheckDate(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={hasRefundedCheckInformation}
                required
              />
            </Grid>
            <Grid
              item
              xs={6}
              style={{ display: 'flex', alignItems: 'center', minHeight: 56 }}
            >
              <Typography variant="body1">
                <strong>Check #:</strong> {refundDetails.checkNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Refund Check #"
                value={refundCheckNumber}
                onChange={(e) => setRefundCheckNumber(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={hasRefundedCheckInformation}
                required
              />
            </Grid>
            <Grid
              item
              xs={6}
              style={{ display: 'flex', alignItems: 'center', minHeight: 56 }}
            >
              <Typography variant="body1">
                <strong>Check Date:</strong>{' '}
                {refundDetails.checkDate
                  ? formatDate(refundDetails.checkDate.toString())
                  : ''}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Refund Amount"
                value={formatCurrency(refundDetails.refundAmount.toString())}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
                required
              />
            </Grid>
            {/* Row 4: Refund Note */}
            <Grid item xs={12}>
              <TextField
                label="Refund Note"
                value={refundNote}
                onChange={(e) => setRefundNote(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                multiline
                minRows={2}
                fullWidth
                disabled={hasRefundedCheckInformation}
              />
            </Grid>
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" mt={5} mb={5}>
            <Typography variant="h6" color="textSecondary">
              No refund details found.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="flex-end" spacing={1}>
          <Grid item>
            <Button onClick={() => close()}>Cancel</Button>
          </Grid>
          {!hasRefundedCheckInformation && (
            <Grid item>
              <Button
                onClick={executeRefund}
                color="primary"
                variant="contained"
                disabled={!refundCheckNumber || !refundCheckDate}
              >
                Execute
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

export default RefundedCheckDetailsModal
